import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Compass, User, LogOut, ScrollText, BellRing, ScanSearch, ShieldCheck, BookOpen, GraduationCap, Wrench, Plane, LayoutTemplate, Brain, Feather, Globe2, Briefcase, Target, BarChart3, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import DisclaimerBar from "@/components/DisclaimerBar";
import DisclaimerGate from "@/components/DisclaimerGate";
import { api } from "@/lib/api";

// Sidebar item → the module_key from the backend so we can hide when disabled by admin.
const SECTIONS = [
  {
    label: "Studio",
    items: [
      { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/app/papers", label: "Papers", icon: BookOpen, testid: "nav-papers", module: "research_papers" },
      { to: "/app/thesis", label: "Thesis", icon: Feather, testid: "nav-thesis", module: "phd_thesis" },
      { to: "/app/consultancy", label: "Consultancy", icon: Briefcase, testid: "nav-consultancy", module: "consultancy" },
    ],
  },
  {
    label: "Discover",
    items: [
      { to: "/app/funding", label: "Funding Feed", icon: Compass, testid: "nav-funding", module: "grant_finder" },
      { to: "/app/travel", label: "Travel Grants", icon: Plane, testid: "nav-travel", module: "travel_grants" },
      { to: "/app/alerts", label: "Alerts", icon: BellRing, testid: "nav-alerts", module: "grant_finder" },
      { to: "/app/analyzer", label: "Analyser", icon: ScanSearch, testid: "nav-analyzer" },
      { to: "/app/discovery", label: "Discovery Coach", icon: Target, testid: "nav-discovery", module: "fellowship_finder" },
      { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
    ],
  },
  {
    label: "Integrity",
    items: [
      { to: "/app/plagiarism", label: "Originality", icon: ShieldCheck, testid: "nav-plagiarism" },
    ],
  },
  {
    label: "Grow",
    items: [
      { to: "/app/learn", label: "Learn", icon: GraduationCap, testid: "nav-learn" },
      { to: "/app/quiz", label: "Quiz", icon: Brain, testid: "nav-quiz" },
      { to: "/app/templates", label: "Templates", icon: LayoutTemplate, testid: "nav-templates" },
      { to: "/app/free-tools", label: "External Tools", icon: Globe2, testid: "nav-free-tools" },
    ],
  },
  {
    label: "You",
    items: [
      { to: "/app/profile", label: "Profile & Dossier", icon: User, testid: "nav-profile" },
    ],
  },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [maintenance, setMaintenance] = useState({ maintenance_mode: false, maintenance_message: "" });

  useEffect(() => {
    api.get("/system/status").then(r => setMaintenance(r.data || {})).catch(()=>{});
  }, []);

  const modules = (user?.modules_enabled) || {};
  const isModuleAllowed = (m) => !m || user?.role === "admin" || modules[m] !== false;

  return (
    <DisclaimerGate>
    <div className="min-h-screen paper flex pb-8 print:pb-0">
      <aside className="w-60 border-r border-border bg-card/50 backdrop-blur-sm hidden md:flex md:flex-col print:hidden">
        <Link to="/app" className="p-6 border-b border-border flex items-center gap-2" data-testid="app-logo">
          <ScrollText className="w-6 h-6 text-primary" />
          <div>
            <div className="font-display text-xl leading-none">GrantForge</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mt-1">For Working Professors</div>
          </div>
        </Link>
        <nav className="flex-1 p-3 overflow-y-auto">
          {SECTIONS.map((section, si) => {
            const items = section.items.filter(i => isModuleAllowed(i.module));
            if (!items.length) return null;
            return (
              <div key={si} className={si === 0 ? "" : "mt-3"}>
                <div className="overline px-2 pb-1 opacity-60">{section.label}</div>
                <div className="space-y-0.5">
                  {items.map((n) => {
                    const active = loc.pathname === n.to || (n.to !== "/app" && loc.pathname.startsWith(n.to));
                    const Icon = n.icon;
                    return (
                      <Link
                        key={n.to}
                        to={n.to}
                        data-testid={n.testid}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-md text-[13px] transition-colors border-l-2",
                          active
                            ? "bg-success/10 text-success border-success font-medium"
                            : "border-transparent text-foreground hover:bg-secondary"
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        {n.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-display text-lg" data-testid="user-avatar">
              {(user?.full_name || "P")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm truncate" data-testid="user-name">{user?.title} {user?.full_name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.institution || "—"}</div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full" data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}>
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        {maintenance.maintenance_mode && user?.role !== "admin" && (
          <div className="bg-destructive/10 border-b border-destructive/30 text-destructive px-6 py-3 flex items-center gap-3" data-testid="maintenance-banner">
            <AlertTriangle className="w-5 h-5 shrink-0"/>
            <div className="text-sm">
              <b>Maintenance in progress —</b> {maintenance.maintenance_message || "please retry in a little while."}
            </div>
          </div>
        )}
        {children}
      </main>
    </div>
    <DisclaimerBar />
    </DisclaimerGate>
  );
}
